import { environment } from '../environments/environment';

import { ApplicationConfig, provideZoneChangeDetection, isDevMode, provideAppInitializer, inject, APP_INITIALIZER } from '@angular/core';
import { provideHttpClient, withFetch, withInterceptorsFromDi } from '@angular/common/http';
import { provideClientHydration, withEventReplay, withI18nSupport } from '@angular/platform-browser';
import { provideRouter, withEnabledBlockingInitialNavigation, withInMemoryScrolling, withRouterConfig } from '@angular/router';

import { routes } from './app.routes';

import { provideTransloco } from '@jsverse/transloco';
import { provideTranslocoLocale } from '@jsverse/transloco-locale';

import { registerLocaleData } from '@angular/common';
import localeFr from '@angular/common/locales/fr';
import { TranslocoHttpLoader } from './transloco-loader';

import { FacebookLoginProvider, GoogleInitOptions, GoogleLoginProvider, SOCIAL_AUTH_CONFIG, SocialAuthServiceConfig } from '@abacritt/angularx-social-login';

import { MainService } from './core/services/main.service';
import { provideAnimations } from '@angular/platform-browser/animations';

import { HTTP_INTERCEPTORS } from '@angular/common/http';
import { TokenInterceptor } from './core/interceptors/token.interceptor';
import { Injector } from '@angular/core';
import { ServiceLocator } from './core/services/locator.service';
import { translocoInitializer } from '../assets/i18n/transloco.initializer'
import { TranslocoService } from '@jsverse/transloco';
import { provideTranslocoMessageformat } from '@jsverse/transloco-messageformat';

registerLocaleData(localeFr);

const fbLoginOptions = {
    scope: 'email',
    return_scopes: true,
    version: 'v10.0'
};

const googleLoginOptions: GoogleInitOptions = {
    scopes: 'email',
    oneTapEnabled: false,
    // prompt: 'select_account'
}

function initializeApp() {
    const mainService = inject(MainService);
    return mainService.bootstrap();
}

function setServiceLocator(injector: Injector) {
    return () => { ServiceLocator.injector = injector; }
}

export const appConfig: ApplicationConfig = {
    providers: [
        provideZoneChangeDetection({ eventCoalescing: true }),
        provideRouter(routes,
            // fix NG05001 error by commenting out withEnabledBlockingInitialNavigation()
            // withEnabledBlockingInitialNavigation(),
            withInMemoryScrolling({
                scrollPositionRestoration: 'enabled',
                anchorScrolling: 'enabled',
            }),
            withRouterConfig({ onSameUrlNavigation: 'reload' })
        ),
        provideClientHydration(withEventReplay(), withI18nSupport()),
        provideHttpClient(withFetch(), withInterceptorsFromDi()),
        provideAnimations(),
        { provide: HTTP_INTERCEPTORS, useClass: TokenInterceptor, multi: true },
        {
            provide: APP_INITIALIZER,
            multi: true,
            useFactory: setServiceLocator,
            deps: [Injector]
        },
        provideTransloco({
            config: {
                availableLangs: ['en', 'fr'],
                defaultLang: 'en',
                fallbackLang: 'en',
                missingHandler: {
                    useFallbackTranslation: true
                },
                reRenderOnLangChange: true,
                prodMode: !isDevMode(),
            },
            loader: TranslocoHttpLoader
        }),
        {
            provide: APP_INITIALIZER,
            useFactory: translocoInitializer,
            deps: [TranslocoService],
            multi: true
        },
        provideTranslocoLocale({
            defaultCurrency: 'CAD',
            defaultLocale: 'en-CA',
            langToLocaleMapping: {
                en: 'en-CA',
                fr: 'fr-CA'
            }
        }),
        provideTranslocoMessageformat(),
        {
            provide: SOCIAL_AUTH_CONFIG,
            useValue: {
                providers: [
                    {
                        id: FacebookLoginProvider.PROVIDER_ID,
                        provider: new FacebookLoginProvider(environment.FACEBOOK_APP_ID, fbLoginOptions),
                    },
                    {
                        id: GoogleLoginProvider.PROVIDER_ID,
                        provider: new GoogleLoginProvider(environment.GOOGLE_APP_ID, googleLoginOptions)
                    }
                ]
            } as SocialAuthServiceConfig
        },
        provideAppInitializer(initializeApp)
    ]
}


